// Main sidebar shell: branding header, restaurant switcher, nav tree.
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState, useEffect, useCallback, useRef } from "react";
import { X, Store, ChevronDown, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { useSidebarMenu } from "./useSidebarMenu";
import { SidebarFooter } from "./SidebarFooter";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_08f655cb-8b91-4c06-80bd-b94d5f4523ca/artifacts/kxmzqj5h_ChatGPT%20Image%2027%20de%20fev.%20de%202026%2C%2001_06_03.png";

export const Sidebar = ({ isOpen, onClose, newOrderCount, newReservationCount, pushEnabled }) => {
  const { logout, user, authAxios, login: authLogin, isSidebarHidden, isOwner, role } = useAuth();
  const authAxiosRef = useRef(authAxios); authAxiosRef.current = authAxios;
  const { t } = useTranslation();
  const { isBlocked } = useSubscription();
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new_pw: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const handleChangePw = useCallback(async () => {
    if (!pwForm.current || !pwForm.new_pw) { toast.error("Preencha todos os campos"); return; }
    if (pwForm.new_pw.length < 4) { toast.error("Nova senha: minimo 4 caracteres"); return; }
    if (pwForm.new_pw !== pwForm.confirm) { toast.error("Senhas nao coincidem"); return; }
    setPwLoading(true);
    try {
      await authAxiosRef.current.put("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.new_pw });
      toast.success("Senha alterada com sucesso!");
      setShowChangePw(false);
      setPwForm({ current: "", new_pw: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao alterar senha");
    }
    setPwLoading(false);
  }, [pwForm]);

  const navigate = useNavigate();
  const location = useLocation();
  const [myRestaurants, setMyRestaurants] = useState([]);
  const [showRestPicker, setShowRestPicker] = useState(false);
  const [activeRestName, setActiveRestName] = useState("");
  const [newRestName, setNewRestName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    authAxiosRef.current.get("/auth/my-restaurants").then(res => {
      setMyRestaurants(res.data.restaurants || []);
      const active = (res.data.restaurants || []).find(r => r.id === res.data.active_restaurant_id);
      if (active) setActiveRestName(active.name);
    }).catch(() => {});
  }, []);

  const switchRestaurant = async (rid) => {
    try {
      const res = await authAxios.post(`/auth/switch-restaurant/${rid}`);
      localStorage.setItem("token", res.data.access_token);
      // Force full reload to ensure all data is refreshed
      window.location.href = "/dashboard";
    } catch (e) {
      console.error("Switch restaurant failed:", e);
    }
  };

  const createNewRestaurant = async () => {
    if (!newRestName.trim()) return;
    setCreating(true);
    try {
      const res = await authAxios.post("/auth/create-restaurant", { name: newRestName });
      localStorage.setItem("token", res.data.access_token);
      window.location.reload();
    } catch { /* ignore */ }
    setCreating(false);
  };

  const menuSections = useSidebarMenu({ role, newOrderCount, newReservationCount });

  const [expandedSection, setExpandedSection] = useState(() => {
    const path = window.location.pathname;
    for (const sec of menuSections) {
      if (sec.children?.some(c => path === c.path || path.startsWith(c.path + "?"))) {
        return sec.id;
      }
    }
    return null;
  });

  const handleNav = (path, newTab) => {
    if (newTab) {
      window.open(path, "_blank");
    } else {
      // Close sidebar FIRST on mobile, then navigate
      if (onClose) onClose();
      // Small delay to let overlay close before page transition
      setTimeout(() => navigate(path), 50);
    }
  };

  const toggleSection = (sec) => {
    if (sec.path) {
      handleNav(sec.path);
    } else {
      setExpandedSection(prev => prev === sec.id ? null : sec.id);
    }
  };

  const isChildActive = (sec) => {
    if (sec.path) return location.pathname === sec.path;
    return sec.children?.some(c => location.pathname === c.path || location.search?.includes(c.path?.split("?")[1]));
  };

  return (
    <div
      className={`w-64 bg-card border-r border-border/50 flex flex-col h-screen fixed left-0 top-0 z-50 transition-transform duration-200 ease-out ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
      data-testid="sidebar"
    >
      {/* Premium Branding Header with Glow */}
      <div className="p-4 border-b border-border/50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-teal-500/5" />
        <div className="relative flex items-center gap-3">
          <div className="relative">
            <div className="absolute -inset-1.5 rounded-xl bg-emerald-500/20 blur-md animate-pulse" />
            <img src={LOGO_URL} alt="Kaiso System" className="relative w-10 h-10 rounded-lg object-cover ring-1 ring-emerald-500/30" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold uppercase tracking-tight leading-none sidebar-brand-glow">
              Kaiso <span className="text-emerald-500">System</span>
            </h1>
            <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-0.5">Restaurant Tech Platform</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition" data-testid="close-sidebar-btn">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Restaurant Switcher */}
      {myRestaurants.length > 0 && (
        <div className="px-3 py-2 border-b border-border/30">
          <button
            onClick={() => setShowRestPicker(!showRestPicker)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition bg-gray-50"
            data-testid="restaurant-switcher"
          >
            <Store className="w-4 h-4 text-emerald-600" />
            <span className="flex-1 text-left truncate">{activeRestName || "Restaurante"}</span>
            {myRestaurants.length > 1 && <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition ${showRestPicker ? "rotate-180" : ""}`} />}
          </button>
          {showRestPicker && (
            <div className="mt-1 space-y-0.5">
              {myRestaurants.map(r => (
                <button key={r.id} onClick={() => switchRestaurant(r.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition ${r.name === activeRestName ? "bg-emerald-50 text-emerald-700 font-medium" : "text-gray-600 hover:bg-gray-100"}`}
                  data-testid={`switch-rest-${r.id?.slice(-6)}`}>
                  <div className="w-5 h-5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{r.name?.charAt(0)}</div>
                  <span className="truncate">{r.name}</span>
                </button>
              ))}
              <div className="flex gap-1 mt-1">
                <input
                  placeholder={t('common.nav.new_restaurant')}
                  value={newRestName}
                  onChange={e => setNewRestName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && createNewRestaurant()}
                  className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-md"
                  data-testid="new-rest-input"
                />
                <button onClick={createNewRestaurant} disabled={creating || !newRestName.trim()}
                  className="px-2 py-1.5 bg-emerald-600 text-white rounded-md text-xs hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="create-rest-btn">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ScrollArea className="flex-1 py-2">
        <nav className="space-y-0.5 px-2" data-testid="sidebar-nav">
          {menuSections
            .filter(sec => !isSidebarHidden(sec.id))
            .map(sec => {
              const active = isChildActive(sec);
              const expanded = expandedSection === sec.id;
              const visibleChildren = sec.children?.filter(child => {
                if (child.ownerOnly && !isOwner) return false;
                const pathKey = child.path.split("/")[1]?.split("?")[0];
                return !isSidebarHidden(pathKey);
              });
              const hasChildren = visibleChildren && visibleChildren.length > 0;
              if (sec.children && (!visibleChildren || visibleChildren.length === 0)) return null;
              return (
                <div key={sec.id}>
                  <button onClick={() => toggleSection(sec)} data-testid={`nav-section-${sec.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${active ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"}`}>
                    <sec.icon className={`w-4 h-4 ${active ? "text-emerald-600" : ""}`} />
                    <span className="flex-1 text-left">{sec.label}</span>
                    {sec.badge > 0 && (
                      <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="order-badge">{sec.badge > 9 ? "9+" : sec.badge}</span>
                    )}
                    {hasChildren && <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />}
                  </button>
                  {hasChildren && expanded && (
                    <div className="ml-4 mt-0.5 mb-1 space-y-0.5 border-l-2 border-emerald-100 pl-3">
                      {visibleChildren.map(child => {
                        const childActive = location.pathname === child.path;
                        return (
                          <button key={child.path} onClick={() => handleNav(child.path, child.newTab)}
                            data-testid={`nav-${child.path.replace(/[/?=]/g, "-").replace(/^-/, "")}`}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-all ${childActive ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"}`}>
                            <span className="flex-1 text-left">{child.label}</span>
                            {child.ai && <span className="text-[8px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">IA</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </nav>
      </ScrollArea>

      <SidebarFooter
        user={user} logout={logout} onClose={onClose} handleNav={handleNav} pushEnabled={pushEnabled}
        showChangePw={showChangePw} setShowChangePw={setShowChangePw}
        pwForm={pwForm} setPwForm={setPwForm} pwLoading={pwLoading} handleChangePw={handleChangePw}
      />
    </div>
  );
};
